import React, { useState, useEffect, useRef } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface AddExclusionModalProps {
  agentId: string;
  entityId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface GroupSearchResult {
  chat_id: string;
  phone_number: string;
  name: string;
}

// Common country codes for Southeast Asia and major markets
const COUNTRY_CODES = [
  { code: '+65', country: 'Singapore 🇸🇬', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia 🇲🇾', flag: '🇲🇾' },
  { code: '+62', country: 'Indonesia 🇮🇩', flag: '🇮🇩' },
  { code: '+63', country: 'Philippines 🇵🇭', flag: '🇵🇭' },
  { code: '+66', country: 'Thailand 🇹🇭', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam 🇻🇳', flag: '🇻🇳' },
  { code: '+86', country: 'China 🇨🇳', flag: '🇨🇳' },
  { code: '+91', country: 'India 🇮🇳', flag: '🇮🇳' },
  { code: '+61', country: 'Australia 🇦🇺', flag: '🇦🇺' },
  { code: '+44', country: 'United Kingdom 🇬🇧', flag: '🇬🇧' },
  { code: '+1', country: 'USA/Canada 🇺🇸', flag: '🇺🇸' },
  { code: '+81', country: 'Japan 🇯🇵', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea 🇰🇷', flag: '🇰🇷' },
];

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const AddExclusionModal: React.FC<AddExclusionModalProps> = ({
  agentId,
  entityId,
  onClose,
  onSuccess
}) => {
  const [exclusionType, setExclusionType] = useState<'group' | 'direct' | 'both' | 'group-by-name'>('group');
  const [participantCountryCode, setParticipantCountryCode] = useState('+65');
  const [participantPhone, setParticipantPhone] = useState('');
  const [directCountryCode, setDirectCountryCode] = useState('+65');
  const [directChatPhone, setDirectChatPhone] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group search state
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupSearchResult | null>(null);
  const [searchResults, setSearchResults] = useState<GroupSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearchQuery = useDebounce(groupSearchQuery, 300);

  // Search groups when debounced query changes
  useEffect(() => {
    const searchGroups = async () => {
      if (debouncedSearchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setSearching(true);
      try {
        const result = await orchestratorApi.searchGroups(debouncedSearchQuery);
        console.log('[Group Search] Result:', result);
        if (result.ok && result.groups) {
          console.log('[Group Search] Found groups:', result.groups.length);
          setSearchResults(result.groups);
          setShowDropdown(true);
        } else {
          console.log('[Group Search] No results or error:', result.error);
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Failed to search groups:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    searchGroups();
  }, [debouncedSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (exclusionType === 'group' && !participantPhone) {
      setError('Participant phone number is required for group exclusions');
      return;
    }

    if (exclusionType === 'direct' && !directChatPhone) {
      setError('Direct chat phone number is required for direct exclusions');
      return;
    }

    if (exclusionType === 'both' && (!participantPhone || !directChatPhone)) {
      setError('Both phone numbers are required for "both" exclusions');
      return;
    }

    if (exclusionType === 'group-by-name' && !selectedGroup) {
      setError('Please select a group chat');
      return;
    }

    setLoading(true);

    try {
      const result = await orchestratorApi.addAgentExclusion(agentId, {
        exclusion_type: exclusionType,
        participant_phone: exclusionType === 'group' || exclusionType === 'both'
          ? `${participantCountryCode}${participantPhone}`
          : undefined,
        direct_chat_phone: exclusionType === 'direct' || exclusionType === 'both'
          ? `${directCountryCode}${directChatPhone}`
          : undefined,
        group_chat_name: exclusionType === 'group-by-name' ? selectedGroup!.name : undefined,
        reason: reason || undefined,
        entity_id: entityId
      });

      if (result.ok) {
        onSuccess();
        handleClose();
      } else {
        setError(result.error || 'Failed to add exclusion');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add exclusion');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setExclusionType('group');
    setParticipantCountryCode('+65');
    setParticipantPhone('');
    setDirectCountryCode('+65');
    setDirectChatPhone('');
    setReason('');
    setError(null);
    setGroupSearchQuery('');
    setSelectedGroup(null);
    setSearchResults([]);
    setShowDropdown(false);
    onClose();
  };

  const handleSelectGroup = (group: GroupSearchResult) => {
    console.log('[Group Select] Selected group:', group);
    setSelectedGroup(group);
    setGroupSearchQuery(group.name);
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleClearGroup = () => {
    setSelectedGroup(null);
    setGroupSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        width: '500px',
        maxWidth: '90%'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem' }}>➕ Add Chat Exclusion</h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: 0,
              color: '#6b7280'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Exclusion Type */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
              🎯 Exclusion Type *
            </label>
            <select
              value={exclusionType}
              onChange={(e) => setExclusionType(e.target.value as 'group' | 'direct' | 'both' | 'group-by-name')}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.9rem',
                marginBottom: '0.5rem'
              }}
              required
            >
              <option value="group">👥 Group Chat (exclude when participant is in group)</option>
              <option value="direct">👤 Direct Chat (exclude 1-to-1 chat)</option>
              <option value="both">🔄 Both (exclude in both group and direct)</option>
              <option value="group-by-name">🏷️ Group by Name (exclude by group chat name)</option>
            </select>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
              {exclusionType === 'group' && 'Agent will ignore group chats containing this participant'}
              {exclusionType === 'direct' && 'Agent will ignore 1-to-1 chats with this phone number'}
              {exclusionType === 'both' && 'Agent will ignore this number in both groups and direct chats'}
              {exclusionType === 'group-by-name' && 'Agent will ignore this specific group chat by name'}
            </p>
          </div>

          {/* Group Chat Name (for group-by-name) */}
          {exclusionType === 'group-by-name' && (
            <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🏷️ Group Chat Name *
              </label>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={groupSearchQuery}
                    onChange={(e) => setGroupSearchQuery(e.target.value)}
                    placeholder="Search group name (e.g., E&H Sales)"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: selectedGroup ? '2px solid #10b981' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      background: selectedGroup ? '#f0fdf4' : 'white'
                    }}
                    required
                  />
                  {selectedGroup && (
                    <>
                      <span style={{
                        padding: '0.75rem',
                        background: '#10b981',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: '600'
                      }}>
                        ✓ Selected
                      </span>
                      <button
                        type="button"
                        onClick={handleClearGroup}
                        style={{
                          padding: '0.75rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>

                {/* Search Results Dropdown */}
                {showDropdown && searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 100,
                    marginTop: '4px'
                  }}>
                    {searchResults.map((group) => (
                      <div
                        key={group.chat_id}
                        onClick={() => handleSelectGroup(group)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{group.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>
                          {group.phone_number}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Searching indicator */}
                {searching && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    padding: '0.75rem',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    marginTop: '4px',
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}>
                    Searching...
                  </div>
                )}

                {/* No results */}
                {!searching && showDropdown && searchResults.length === 0 && debouncedSearchQuery.trim().length >= 2 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    padding: '0.75rem',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    marginTop: '4px',
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: '#6b7280'
                  }}>
                    No groups found
                  </div>
                )}
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', margin: 0 }}>
                Search and select a group chat by name (e.g., "E&H Sales")
              </p>
            </div>
          )}

          {/* Participant Phone (for group/both) */}
          {(exclusionType === 'group' || exclusionType === 'both') && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                📱 Participant Phone Number *
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={participantCountryCode}
                  onChange={(e) => setParticipantCountryCode(e.target.value)}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    minWidth: '160px',
                    background: 'white'
                  }}
                >
                  {COUNTRY_CODES.map(cc => (
                    <option key={cc.code} value={cc.code}>
                      {cc.flag} {cc.code}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={participantPhone}
                  onChange={(e) => setParticipantPhone(e.target.value)}
                  placeholder="8123 4567"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                  required
                />
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', margin: 0 }}>
                Phone number to exclude in group chats (e.g., 8123 4567)
              </p>
            </div>
          )}

          {/* Direct Chat Phone (for direct/both) */}
          {(exclusionType === 'direct' || exclusionType === 'both') && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                📞 Direct Chat Phone Number *
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={directCountryCode}
                  onChange={(e) => setDirectCountryCode(e.target.value)}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    minWidth: '160px',
                    background: 'white'
                  }}
                >
                  {COUNTRY_CODES.map(cc => (
                    <option key={cc.code} value={cc.code}>
                      {cc.flag} {cc.code}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={directChatPhone}
                  onChange={(e) => setDirectChatPhone(e.target.value)}
                  placeholder="8123 4567"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                  required
                />
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', margin: 0 }}>
                Phone number to exclude in 1-to-1 chats (e.g., 8123 4567)
              </p>
            </div>
          )}

          {/* Reason */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
              📝 Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Operations team member, use Operations Agent instead"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.9rem',
                minHeight: '80px',
                resize: 'vertical'
              }}
            />
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', margin: 0 }}>
              Explain why this exclusion exists (helps with documentation)
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#991b1b',
              fontSize: '0.85rem',
              marginBottom: '1rem'
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                background: loading ? '#d1d5db' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? '⏳ Adding...' : '➕ Add Exclusion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
